import React, { useState } from "react";
import {
  Typography,
  Row,
  Col,
  Card,
  Button,
  Tooltip,
  message,
  Modal,
  Input,
} from "antd";
import { Container, BannerCard } from "./elements";
import { useQuery, useMutation } from "@apollo/client";
import { GET_BANNERS, DELETE_BANNER } from "./requests";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import CreateBanner from "./components/createModal";
import EditBanner from "./components/editModal";
import DetailsBanner from "./components/detailsModal";
import { bannerTypes } from "../../types";
import shortid from "shortid";

const { Title } = Typography;
const { Meta } = Card;

const Banners: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState<any>(null);
  const [titleFilter, setTitleFilter] = useState<string>("");

  const [deleteBannerMutation] = useMutation(DELETE_BANNER);

  const {
    data: bannersData,
    refetch,
    loading,
  } = useQuery(GET_BANNERS, {
    variables: {
      params: { page: 1,
        pageSize: 100 },
      title: titleFilter,
    },
  });

  const showDeleteConfirm = (banner: typeof bannerTypes.banner) => {
    Modal.confirm({
      title: "¿Estás seguro de eliminar este banner?",
      content: `El banner ${banner.title} será eliminado.`,
      okText: "Sí, eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: () => handleDeleteCategory(banner.id),
    });
  };

  const handleDeleteCategory = async (bannerId: string) => {
    try {
      await deleteBannerMutation({
        variables: { id: bannerId },
      });
      message.success("Categoría eliminada correctamente");
      await refetch();
    } catch (error) {
      console.log(error);
      message.error("Error al eliminar la categoría");
    }
  };

  return (
    <Container>
      {/* HEADER & SEARCH */}
      <Row gutter={16} justify="space-between" style={{ marginBottom: 20 }}>
        <Col flex="auto">
          <Input
            size="large"
            placeholder="Buscar por Nombre"
            value={titleFilter}
            onChange={(e) => setTitleFilter(e.target.value)}
            allowClear
            style={{ width: "40%",
              maxWidth: 300 }}
          />
        </Col>
        <Col>
          <Button
            size="large"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setShowModal(true)}
          >
            Crear Banner
          </Button>
        </Col>
      </Row>

      <Row
        gutter={[32, 32]} // Adds spacing between columns and rows
        justify="space-between" // Distributes cards evenly across full width
        style={{
          marginTop: 10,
          width: "100%", // Ensures the row spans the full container
          display: "flex",
          flexWrap: "wrap", // Prevents overlapping
        }}
      >
        {bannersData?.banners?.results?.map((row: any) => (
          <Col
            xs={24}
            sm={12}
            md={8}
            lg={6}
            key={row.id}
            style={{
              display: "flex",
              justifyContent: "center",
              flex: "1 1 30%", // Ensures 3 items per row without shrinking
              maxWidth: "400px", // Keeps original size
            }}
          >
            <BannerCard
              style={{
                borderRadius: 16,
                border: "1px solid #EAEAEA",
                boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
                overflow: "hidden",
                transition: "transform 0.2s ease-in-out",
                width: "100%", // Ensures cards keep original size
                maxWidth: "350px", // Prevents shrinking
              }}
              hoverable
              cover={
                <div
                  style={{
                    height: 200,
                    overflow: "hidden",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={row.img}
                    alt={row.title}
                    style={{
                      maxWidth: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              }
              actions={[
                <Tooltip title="Ver Productos" key={shortid.generate()}>
                  <Button
                    type="primary"
                    icon={<UnorderedListOutlined />}
                    shape="circle"
                    size="middle"
                    onClick={() => {
                      setSelectedBanner(row);
                      setShowDetailsModal(true);
                    }}
                  />
                </Tooltip>,
                <Tooltip title="Editar" key={shortid.generate()}>
                  <Button
                    type="default"
                    icon={<EditOutlined />}
                    shape="circle"
                    size="middle"
                    onClick={() => {
                      setSelectedBanner(row);
                      setShowEditModal(true);
                    }}
                  />
                </Tooltip>,
                <Tooltip title="Eliminar" key={shortid.generate()}>
                  <Button
                    type="dashed"
                    danger
                    icon={<DeleteOutlined />}
                    shape="circle"
                    size="middle"
                    onClick={() => showDeleteConfirm(row)}
                  />
                </Tooltip>,
              ]}
            >
              <Meta
                title={
                  <Title
                    level={5}
                    ellipsis={{
                      rows: 1,
                      expandable: false,
                    }}
                  >
                    {row.title}
                  </Title>
                }
                description={
                  <span style={{ fontSize: "13px",
                    color: "#777" }}>
                    {row.type}
                  </span>
                }
              />
            </BannerCard>
          </Col>
        ))}
      </Row>

      {/* MODALS */}
      <CreateBanner
        showModal={showModal}
        setShowModal={setShowModal}
        refetch={refetch}
      />
      <EditBanner
        showModal={showEditModal}
        setShowModal={setShowEditModal}
        banner={selectedBanner}
        setBanner={setSelectedBanner}
        refetch={refetch}
      />
      <DetailsBanner
        showModal={showDetailsModal}
        setShowModal={setShowDetailsModal}
        banner={selectedBanner}
        setBanner={setSelectedBanner}
        loading={loading}
      />
    </Container>
  );
};

export default Banners;
